import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';

function SeoHead({
  title,
  description,
  keywords,
  canonicalUrl,
  jsonLd,
}: {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  jsonLd?: Record<string, unknown>;
}) {
  useEffect(() => {
    document.title = title;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', description);
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', keywords);
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', canonicalUrl);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description);
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl);
    const twitterTitle = document.querySelector('meta[name="twitter:title"]');
    if (twitterTitle) twitterTitle.setAttribute('content', title);
    const twitterDesc = document.querySelector('meta[name="twitter:description"]');
    if (twitterDesc) twitterDesc.setAttribute('content', description);

    if (jsonLd) {
      const existingScript = document.getElementById('page-jsonld');
      if (existingScript) existingScript.remove();
      const script = document.createElement('script');
      script.id = 'page-jsonld';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    }
  }, [title, description, keywords, canonicalUrl, jsonLd]);

  return null;
}

const milestones = [
  { year: '2018', title: '品牌创立', description: '问鼎.cc 正式创立，以"让每个品牌都能在数字世界绽放光彩"为使命，开启品牌数字化服务之路。' },
  { year: '2019', title: '平台上线', description: '问鼎.cc 官方平台 1.0 版本正式上线，首批服务覆盖域名管理与品牌展示两大核心模块。' },
  { year: '2020', title: '安全体系建立', description: '引入国际领先的安全防护体系，通过多项安全认证，为用户数字资产提供全方位保护。' },
  { year: '2021', title: '服务升级', description: '平台用户突破 10,000，推出企业级定制化服务方案，获得行业广泛认可。' },
  { year: '2022', title: '技术革新', description: '完成底层技术架构全面升级，引入 AI 智能分析引擎，平台性能与安全性大幅提升。' },
  { year: '2023', title: '生态拓展', description: '与多家行业头部企业达成战略合作，构建品牌数字化服务生态体系。' },
  { year: '2024', title: '用户突破', description: '累计服务用户突破 50,000，覆盖全国 50+ 城市，成为行业信赖品牌。' },
  { year: '2025', title: '持续创新', description: '持续加大技术研发投入，探索前沿技术应用，为用户创造更大价值。' },
];

const trustIndicators = [
  { icon: 'ri-shield-check-line', title: '安全认证', desc: '通过多项国际安全标准认证' },
  { icon: 'ri-award-line', title: '行业认可', desc: '荣获多项行业权威奖项' },
  { icon: 'ri-user-heart-line', title: '用户信赖', desc: '累计服务超过 50,000 用户' },
  { icon: 'ri-building-2-line', title: '企业合作', desc: '与 200+ 企业建立长期合作' },
];

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9teg3gjm6rqefe3vk10', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* raw response */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          (parsed?.meta as Record<string, string>)?.message ||
          (parsed?.meta as Record<string, string>)?.detail ||
          (parsed?.message as string) ||
          responseText ||
          '提交失败，请稍后重试';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于我们 - 问鼎.cc',
    description: '了解问鼎.cc的品牌故事、发展历程、核心优势与联系方式。专业的品牌官方平台，致力于品牌数字化服务。',
    url: 'https://hecaoqing.com.cn/about',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: '首页', item: 'https://hecaoqing.com.cn/' },
        { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://hecaoqing.com.cn/about' },
      ],
    },
  };

  return (
    <>
      <SeoHead
        title="关于我们 - 问鼎.cc | 品牌介绍 联系方式"
        description="了解问鼎.cc的品牌故事、发展历程、核心优势与联系方式。问鼎.cc 是专业的品牌官方平台，致力于为企业和个人提供安全、稳定、高效的品牌数字化服务。"
        keywords="问鼎.cc,关于问鼎cc,问鼎cc品牌介绍,问鼎cc联系方式"
        canonicalUrl="https://hecaoqing.com.cn/about"
        jsonLd={aboutJsonLd}
      />

      <main className="pt-16 min-h-screen bg-background-50">
        {/* Page Header */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-7xl mx-auto px-6 py-12 md:py-16">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: '关于我们' },
              ]}
            />
            <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mt-2">
              关于问鼎.cc
            </h1>
            <p className="text-sm text-foreground-500 mt-3 max-w-2xl leading-relaxed">
              专业的品牌官方平台，致力于为企业和个人提供安全、稳定、高效的品牌数字化服务
            </p>
          </div>
        </section>

        {/* Brand Intro */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                  我们的故事
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">问鼎.cc</strong> 诞生于数字化浪潮蓬勃发展的时代。我们深知，每一个品牌都需要一个值得信赖的线上阵地，一个能够承载品牌价值、连接用户信任的数字化平台。
                  </p>
                  <p>
                    自创立以来，问鼎.cc 始终秉持"让每个品牌都能在数字世界绽放光彩"的使命，致力于为企业用户和个人用户提供专业、安全、高效的品牌数字化解决方案。
                  </p>
                  <p>
                    我们拥有一支经验丰富的技术与运营团队，持续投入研发创新，建立了覆盖品牌展示、域名管理、安全防护、数据分析等核心功能的一站式服务平台。
                  </p>
                  <p>
                    截至目前，问鼎.cc 已累计服务 <strong className="text-foreground-900">50,000+</strong> 用户，与 <strong className="text-foreground-900">200+</strong> 家企业建立长期合作关系，服务网络覆盖全国 <strong className="text-foreground-900">50+</strong> 个城市。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20office%20space%20with%20warm%20coral%20and%20gold%20accent%20colors%2C%20brand%20story%20visual%2C%20professional%20team%20working%20environment%2C%20soft%20natural%20lighting%20through%20large%20windows%2C%20clean%20geometric%20decor%2C%20elegant%20and%20aspirational%20atmosphere%2C%20no%20people%20visible&width=800&height=600&seq=about-brand-intro&orientation=landscape"
                  alt="问鼎.cc 品牌故事"
                  className="w-full h-full object-cover"
                  title="问鼎.cc 品牌故事 - 问鼎.cc"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                品牌信誉
              </h2>
              <p className="text-sm text-foreground-500">
                品质与信任，是问鼎.cc 不懈的追求
              </p>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {trustIndicators.map((item) => (
                <div key={item.title} className="text-center p-8 rounded-2xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-xl bg-primary-50 text-primary-500">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1 font-heading">{item.title}</h3>
                  <p className="text-xs text-foreground-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-24 bg-background-100">
          <div className="max-w-4xl mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                发展历程
              </h2>
              <p className="text-sm text-foreground-500">
                一路走来，每一步都坚实有力
              </p>
            </div>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-0 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:translate-x-[-0.5px]"></div>

              <div className="flex flex-col gap-10">
                {milestones.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex items-start gap-6 ${
                      index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Dot */}
                    <div className="absolute left-[-5px] md:left-1/2 md:translate-x-[-5px] top-2 w-2.5 h-2.5 rounded-full bg-primary-500 border-2 border-background-50 z-10"></div>

                    {/* Content */}
                    <div className={`ml-8 md:ml-0 ${index % 2 === 0 ? 'md:pr-12 md:text-right md:w-1/2' : 'md:pl-12 md:w-1/2'}`}>
                      <span className="inline-block text-sm font-bold text-primary-500 mb-1">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-900 mb-1 font-heading">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.description}</p>
                    </div>

                    {index % 2 === 0 && <div className="hidden md:block md:w-1/2"></div>}
                    {index % 2 !== 0 && <div className="hidden md:block md:w-1/2"></div>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-16 md:py-24 bg-background-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-6">
                  联系我们
                </h2>
                <p className="text-sm text-foreground-500 leading-relaxed mb-8">
                  如果您有任何问题、建议或合作意向，欢迎通过以下方式与我们取得联系。我们的专业团队将竭诚为您服务。
                </p>

                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">电子邮箱</h4>
                      <p className="text-sm text-foreground-500">support@hecaoqing.com.cn</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">客服热线</h4>
                      <p className="text-sm text-foreground-500">工作日 9:00 - 18:00</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-wechat-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">微信公众号</h4>
                      <p className="text-sm text-foreground-500">搜索"问鼎CC"关注我们</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 shrink-0">
                      <i className="ri-map-pin-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground-900 mb-0.5">办公地址</h4>
                      <p className="text-sm text-foreground-500">中国 · 在线服务，覆盖全国</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-background-100 rounded-2xl border border-background-200/70 p-8">
                <h3 className="text-xl font-bold font-heading text-foreground-900 mb-6">
                  发送消息
                </h3>

                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="spam-field"
                  />

                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      您的姓名 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的姓名"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      电子邮箱 <span className="text-primary-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入您的邮箱"
                    />
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                      placeholder="请输入消息主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      消息内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-200 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                      placeholder="请输入您的消息内容（最多500字）"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>

                  {formStatus === 'success' && (
                    <div className="text-sm text-green-600 bg-green-50 px-4 py-2.5 rounded-lg text-center">
                      消息已发送！我们会尽快与您联系，感谢您的关注。
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="text-sm text-red-600 bg-red-50 px-4 py-2.5 rounded-lg text-center">
                      {formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-20 bg-background-100">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <h2 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-4">
              加入问鼎.cc，开启品牌数字化之旅
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-lg mx-auto leading-relaxed">
              专业的品牌官方平台，为您提供全方位支持。立即注册，体验便捷、安全、高效的品牌数字化服务。
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/"
                className="px-8 py-3 rounded-lg bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即注册
              </Link>
              <Link
                to="/category/brand-news"
                className="px-8 py-3 rounded-lg border border-background-300 text-foreground-700 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}